import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Search, Activity } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";

const PAGE_SIZE = 30;

const AdminActivityLog = () => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);

  const { data: logs, isLoading } = useQuery({
    queryKey: ["admin-activity-log"],
    queryFn: async () => {
      const { data, error } = await supabase.from("admin_activity_log").select("*").order("created_at", { ascending: false }).limit(500);
      if (error) throw error;
      return data;
    },
  });

  const filtered = useMemo(() => {
    if (!logs) return [];
    if (!search) return logs;
    const q = search.toLowerCase();
    return logs.filter((l) =>
      l.action.toLowerCase().includes(q) || l.entity_type.toLowerCase().includes(q) || l.user_email.toLowerCase().includes(q)
    );
  }, [logs, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const paged = filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  const actionLabels: Record<string, string> = {
    updated_order_status: "Updated order status",
    created_discount: "Created discount code",
    deleted_discount: "Deleted discount code",
    duplicated_product: "Duplicated product",
    archived_product: "Archived product",
    bulk_update: "Bulk updated products",
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold">Activity Log</h1>
          <p className="text-sm text-muted-foreground font-body">{filtered.length} entries</p>
        </div>
      </div>

      <div className="relative mb-6 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input placeholder="Search actions…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} className="pl-10" />
      </div>

      {isLoading ? (
        <div className="text-muted-foreground text-center py-20">Loading…</div>
      ) : (
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <div className="divide-y divide-border">
            {paged.map((log) => (
              <div key={log.id} className="px-5 py-3 flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center shrink-0 mt-0.5">
                  <Activity className="w-4 h-4 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-body text-sm">
                    <span className="font-medium">{log.user_email}</span>{" "}
                    <span className="text-muted-foreground">{actionLabels[log.action] || log.action}</span>
                    {log.entity_id && <span className="text-muted-foreground"> · {log.entity_type} {log.entity_id.slice(0, 8)}…</span>}
                  </p>
                  {log.details && (
                    <p className="text-xs text-muted-foreground font-body mt-0.5">
                      {typeof log.details === "object" ? JSON.stringify(log.details) : String(log.details)}
                    </p>
                  )}
                </div>
                <span className="text-xs text-muted-foreground font-body shrink-0">
                  {format(new Date(log.created_at), "dd MMM, HH:mm")}
                </span>
              </div>
            ))}
          </div>
          {filtered.length === 0 && (
            <div className="text-center py-12 text-muted-foreground font-body">
              <Activity className="w-10 h-10 mx-auto mb-3 opacity-40" />No activity recorded yet
            </div>
          )}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border">
              <p className="text-sm text-muted-foreground font-body">Page {safePage + 1} of {totalPages}</p>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" disabled={safePage === 0} onClick={() => setPage(safePage - 1)}>Prev</Button>
                <Button size="sm" variant="ghost" disabled={safePage >= totalPages - 1} onClick={() => setPage(safePage + 1)}>Next</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminActivityLog;
